import React, { useCallback, useEffect, useState } from 'react';
import Dropzone from 'react-dropzone';
import Icon from '../icons';
import { useUsersContext } from '../../context/usersContext';
import { useUserInfo } from "../../context/user";

const MyDropzone = ({ saveDropzoneFiles, selectionData, isSharedKnowledge = false, onClose, uploadProgress = null, uploadDone = null, initialFiles = null }) => {
  const MAX_FILE_SIZE_MB = 512;
  const userInfo = useUserInfo()[0];
  const userActiveLocation = useUserInfo()[4];
  const [files, setFiles] = useState([]);
  const [errorMessage, setErrorMessage] = useState('');
  const [loader, setLoader] = useState(false);
  const [firstscreen, setFirstscreen] = useState(false);
  const [audioFileChecks, setAudioFileChecks] = useState({});
  const [selectedOption, setSelectedOption] = useState([]);
  const [loadingComplete, setLoadingComplete] = useState(false);
  const [assistantError, setAssistantError] = useState(false)
  const [showAgentStep, setShowAgentStep] = useState(false)

  const allowedTypes = [
    // Audio
    "audio/mpeg",
    "audio/mp4",
    "audio/mpga",
    "audio/wav",

    // Video
    "video/mp4",
    "video/mpeg",
    "video/webm",

    // Spreadsheets (CU-86canfd8w: restore CSV/spreadsheet support dropped
    // by the #228 upload-modal redesign — Data Interpreter is built around
    // these files, and the backend places no restriction on file type)
    "text/csv",
    "text/tab-separated-values",
    "application/vnd.ms-excel",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",

    // Text & documents
    "text/plain",
    "text/html",
    "text/css",
    "text/javascript",
    "text/markdown",
    "application/json",
    "application/pdf",
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "application/vnd.openxmlformats-officedocument.presentationml.presentation",
    "application/x-sh",
    "application/typescript",

    // Source code
    "text/x-c",
    "text/x-c++",
    "text/x-csharp",
    "text/x-golang",
    "text/x-java",
    "text/x-php",
    "text/x-python",
    "text/x-script.python",
    "text/x-ruby",
    "text/x-tex"
  ];

  const audiofilestype = [
    // Audio
    "audio/mpeg",   // mp3
    "audio/mp4",    // m4a
    "audio/mpga",   // mpga
    "audio/wav",    // wav

    // Video
    "video/mp4",
    "video/mpeg",
    "video/webm"
  ];

  useEffect(() => {
    // File objects can't be serialized to JSON, so any persisted entry would
    // come back as an empty object (showing no name / "NaN MB"). Clear stale data.
    localStorage.removeItem('uploadedFiles');
  }, []);

  useEffect(() => {
    // Files picked from Google Drive / Dropbox arrive pre-selected. Seed them
    // into the same list the computer-upload flow uses so third-party uploads
    // go through the identical "Assign Agents → processing" modal instead of a
    // separate, plainer one. The third-party pickers already restrict file
    // types, so we skip the drag-and-drop type filter here.
    if (initialFiles && initialFiles.length > 0) {
      const seeded = initialFiles.map(item => {
        const file = item?.file ? item.file : item;
        const sizeMb = file?.size ? file.size / (1024 * 1024) : 0;
        return {
          file,
          progress: 100,
          completed: true,
          tooLarge: sizeMb > MAX_FILE_SIZE_MB,
        };
      });
      setFiles(seeded);
      setFirstscreen(true);
      setLoadingComplete(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveFilesToStorage = () => {
    setLoader(false);
  };

  const onDrop = useCallback(acceptedFiles => {
    let errorMessages = ''; 

    if (files.length + acceptedFiles.length > 10) {
      errorMessages += `<p style="color:hsl(var(--destructive));">You can only upload a maximum of 10 files at a time.</p>`;
      setErrorMessage(errorMessages);
      return;
    }

    const filteredFiles = acceptedFiles.filter(file => allowedTypes.includes(file.type));

    if (filteredFiles.length !== acceptedFiles.length) {
      const invalidFiles = acceptedFiles.filter(file => !allowedTypes.includes(file.type));
      const invalidFileNames = invalidFiles.map(file => file.name).join(', ');
      errorMessages += `<p style="color:hsl(var(--destructive));">Invalid files (either unsupported types): <br></br>${invalidFileNames}</p>`

    }
    if (errorMessages !== '') {
      setErrorMessage(errorMessages);
      return; // Stop processing further if any errors occurred
    }
    // Oversized files are kept in the list but flagged so they render the red
    // warning card and get excluded from the actual upload.
    const updatedFiles = filteredFiles.map(file => ({
      file,
      progress: 100,
      completed: true,
      tooLarge: file.size / (1024 * 1024) > MAX_FILE_SIZE_MB
    }));

    const allFiles = [...files, ...updatedFiles];
    setFiles(allFiles);
    saveFilesToStorage(allFiles);

    if (errorMessage !== '') {
      setErrorMessage('');
    }
    setFirstscreen(true);
    setLoadingComplete(true);
  }, [files, saveFilesToStorage]);


  const handlesave = e => {
    e.preventDefault();
    const validFiles = files.filter(file => !file.tooLarge);
    if (validFiles.length === 0) {
      setErrorMessage('<p style="color:hsl(var(--destructive));">No valid files to upload. Remove oversized files and try again.</p>');
      return;
    }
    const filesWithCheckboxes = validFiles.map(file => ({
      ...file,
      isChecked: audioFileChecks[file.file.name] || false
    }));
    if (isSharedKnowledge) {
      saveDropzoneFiles(filesWithCheckboxes, []);
      setLoader(true);
      localStorage.removeItem('uploadedFiles');
      return;
    }
    if (selectedOption.length > 0) {

      saveDropzoneFiles(filesWithCheckboxes, selectedOption);
      setLoader(true);
      localStorage.removeItem('uploadedFiles');
      setAssistantError(false)
    } else {
      setAssistantError(true)
    }
  };

  const handleDelete = fileToDelete => {
    const updatedFiles = files.filter(file => file.file !== fileToDelete);
    setFiles(updatedFiles);
    saveFilesToStorage(updatedFiles);
    if (updatedFiles.length < 1) {
      setFirstscreen(false);
      setShowAgentStep(false);
    }
  };


  const handleNext = (e) => {
    if (isSharedKnowledge) {
      handlesave(e);
    } else {
      setShowAgentStep(true);
    }
  };

  const handleCheckboxChange = (fileName, isChecked) => {
    setAudioFileChecks({
      ...audioFileChecks,
      [fileName]: isChecked
    });
  };

  const restart = () => {
    setFiles([]);
    saveFilesToStorage([]);
    setFirstscreen(false);
  };



  const agentOptions = (selectionData || [])
    .filter(item => !(item.readonly && item.assist_type !== "account-only"))
    .map(item => ({ label: item.assistant_name, value: item.assistant_id }));

  useEffect(() => {
    console.log("selectionData:", selectionData);
  }, [selectionData]);
    console.log("agentOptions:", agentOptions);

  const toggleAgent = (option) => {
    setSelectedOption(prev => {
      const exists = prev.some(p => p.value === option.value);
      return exists ? prev.filter(p => p.value !== option.value) : [...prev, option];
    });
    setAssistantError(false);
  };

  const allAgentsSelected =
    agentOptions.length > 0 && selectedOption.length === agentOptions.length;

  const toggleSelectAllAgents = () => {
    setSelectedOption(allAgentsSelected ? [] : [...agentOptions]);
    setAssistantError(false);
  };

  const validFileCount = files.filter(file => !file.tooLarge).length;

  const acceptMap = allowedTypes.reduce((acc, t) => ({ ...acc, [t]: [] }), {});

  return (
    <Dropzone onDrop={onDrop} accept={acceptMap} noClick noKeyboard>
      {({ getRootProps, getInputProps, open }) => (
        <div {...getRootProps()} className="kb-upload">
          <input {...getInputProps()} />

          {onClose && (
            <button
              type="button"
              className="kb-modal-close"
              onClick={(e) => {
                e.stopPropagation();
                onClose();
              }}
            >
              <Icon id="close" />
            </button>
          )}

          {(() => {
            if (uploadDone) {
              return (
                <div className="kb-upload-head">
                  <b className="kb-upload-title">Files Ready</b>
                </div>
              );
            }
            const isProcessing = files.length > 0 && loader;
            const isAssignAgents = files.length > 0 && !loader && showAgentStep && !isSharedKnowledge;
            if (isProcessing) {
              return (
                <div className="kb-upload-head">
                  <b className="kb-upload-title">Processing Files</b>
                </div>
              );
            }
            if (isAssignAgents) {
              return (
                <>
                  <div className="kb-upload-head">
                    <b className="kb-upload-title">Assign Agents</b>
                  </div>
                  <p className="kb-upload-desc">
                    Choose the agents that should have access to these documents.
                    Each document will be added to all selected agents.
                  </p>
                </>
              );
            }
            return (
              <>
                <div className="kb-upload-head">
                  <b className="kb-upload-title">Upload files</b>
                </div>
                <p className="kb-upload-desc">
                  {isSharedKnowledge
                    ? "Upload up to 10 files at once. This will be added to all current and future AI Agents."
                    : "Upload up to 10 files at once. You'll choose which agents should use them next."}
                </p>
              </>
            );
          })()}

          {uploadDone && (
            <>
              {uploadDone.crashfiles && uploadDone.crashfiles.length > 0 ? (
                <>
                  <p className="kb-ready-text" style={{ color: "hsl(var(--destructive))" }}>
                    {uploadDone.docs - uploadDone.crashfiles.length} document
                    {uploadDone.docs - uploadDone.crashfiles.length === 1 ? "" : "s"} added.
                    The following file{uploadDone.crashfiles.length === 1 ? "" : "s"} could not be processed:
                  </p>
                  <ul className="kb-ready-failed">
                    {uploadDone.crashfiles.map((name, index) => (
                      <li key={index}>{name}</li>
                    ))}
                  </ul>
                </>
              ) : (
                <p className="kb-ready-text">
                  {uploadDone.docs} document{uploadDone.docs === 1 ? "" : "s"}{" "}
                  {uploadDone.docs === 1 ? "has" : "have"} been added to{" "}
                  {isSharedKnowledge
                    ? "the shared knowledge base"
                    : `${uploadDone.agents} agent${uploadDone.agents === 1 ? "" : "s"}`}
                  .
                </p>
              )}
              <div className="kb-footer kb-footer--start">
                <button
                  type="button"
                  className="kb-done-btn"
                  onClick={() => onClose?.()}
                >
                  Done
                </button>
              </div>
            </>
          )}

          {!uploadDone && files.length === 0 && (
            <>
              <button type="button" className="kb-choose-btn" onClick={open}>
                Choose Files
              </button>
              <p className="kb-upload-hint">
                Each file can be up to 512 MB. Larger files may take longer to process.
              </p>
              {errorMessage && (
                <div className="kb-error" dangerouslySetInnerHTML={{ __html: errorMessage }}></div>
              )}
            </>
          )}

          {!uploadDone && files.length > 0 && loader && (
            <div className="kb-processing">
              <p className="kb-processing-text">
                We're adding {validFileCount} document{validFileCount === 1 ? "" : "s"}
                {isSharedKnowledge
                  ? " to the shared knowledge base"
                  : ` to ${selectedOption.length} agent${selectedOption.length === 1 ? "" : "s"}`}
                . This may take a few minutes. You can close this and keep working.
              </p>


              <div className="kb-spinner" />
              <p className="kb-processing-sub">Indexing and preparing files...</p>
              <div className="kb-footer kb-footer--end">
                <button type="button" className="kb-next-btn" onClick={() => onClose?.()}>
                  Close
                </button>
              </div>
            </div>
          )}

          {files.length > 0 && !loader && showAgentStep && !isSharedKnowledge && (
            <>
              <div className="kb-agent-list">
                {agentOptions.length > 0 ? (
                  <>
                    <label className="kb-agent-row">
                      <span className="kb-agent-name">Select All Agents</span>
                      <input
                        type="checkbox"
                        className="kb-agent-checkbox"
                        checked={allAgentsSelected}
                        onChange={toggleSelectAllAgents}
                      />
                    </label>
                    {agentOptions.map((option, index) => {
                    const checked = selectedOption.some(p => p.value === option.value);
                    return (
                      <label key={index} className="kb-agent-row">
                        <span className="kb-agent-name">{option.label}</span>
                        <input
                          type="checkbox"
                          className="kb-agent-checkbox"
                          checked={checked}
                          onChange={() => toggleAgent(option)}
                        />
                      </label>
                    );
                  })}
                  </>
                ) : (
                  <div className="kb-agent-loading">
                    <div className="kb-spinner kb-spinner--blue" />
                    <p className="kb-agent-loading-text">Loading agents…</p>
                  </div>
                )}
              </div>
              {assistantError && (
                <span style={{ color: "hsl(var(--destructive))", fontSize: "13px" }}>Please select an AI Agent *</span>
              )}
              <div className="kb-footer">
                <span className="kb-selected-count">
                  <b>{selectedOption.length}</b> Agent{selectedOption.length === 1 ? "" : "s"} selected
                </span>
                <button type="button" className="kb-next-btn" onClick={handlesave}>
                  Start processing
                </button>
              </div>
            </>
          )}

          {files.length > 0 && !loader && !(showAgentStep && !isSharedKnowledge) && (
            <>
              <p className="kb-upload-hint kb-upload-hint--top">
                Each file can be up to 512 MB. Larger files may take longer to process.
              </p>

              <div className="kb-file-list">
                {files.map((uploadedFile, index) => (
                  <div
                    key={index}
                    className={`kb-file-card${uploadedFile.tooLarge ? " kb-file-card--error" : ""}`}
                  >
                    <div className="kb-file-info">
                      <p className="kb-file-name">{uploadedFile.file.name}</p>
                      {uploadedFile.tooLarge ? (
                        <p className="kb-file-warning">
                          {parseFloat(uploadedFile.file.size / (1024 * 1024)).toFixed(2)} MB. File size too large. This file will not be processed.
                        </p>
                      ) : (
                        <p className="kb-file-size">
                          {parseFloat(uploadedFile.file.size / (1024 * 1024)).toFixed(2)} MB
                        </p>
                      )}
                      {!uploadedFile.tooLarge && audiofilestype.includes(uploadedFile.file.type) && (
                        <label className="kb-translate">
                          <input
                            type="checkbox"
                            checked={audioFileChecks[uploadedFile.file.name] || false}
                            onChange={(e) => handleCheckboxChange(uploadedFile.file.name, e.target.checked)}
                          />
                          Translate
                        </label>
                      )}
                    </div>
                    <button
                      type="button"
                      className="kb-file-delete"
                      onClick={() => handleDelete(uploadedFile.file)}
                    >
                      <Icon id="delete" />
                    </button>
                  </div>
                ))}
              </div>

              {errorMessage && (
                <div className="kb-error" dangerouslySetInnerHTML={{ __html: errorMessage }}></div>
              )}

              <div className="kb-footer">
                <button type="button" className="kb-add-btn" onClick={open}>
                  Add Files
                </button>
                <button type="button" className="kb-next-btn" onClick={handleNext}>
                  Next
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </Dropzone>
  );
};

export default MyDropzone;
